/**
 * Delete a page, behind typing its slug.
 *
 * Deleting a page orphans every announcement and tour keyed to its slug, so the confirmation is
 * deliberate rather than a yes/no. Deleting the main page is called out separately: the BU is
 * left with nowhere to deliver announcements that have no page of their own.
 *
 * @module Pages/BUs/Pages
 */

import { css } from '@emotion/css';
import { UTButton, UTModal, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import type { Page } from '../../Types/Pages.types';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const textStyle = css`
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const alertStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
  border: 1px solid var(--color-warning);
`;

const alertTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--gl-yellow-dark);
`;

const alertTextStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

export interface DeletePageModalProps {
  open: boolean;
  page?: Page;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: (page: Page) => void;
}

export const DeletePageModal = ({
  open,
  page,
  isDeleting,
  onClose,
  onConfirm,
}: DeletePageModalProps): JSX.Element => {
  const [typedSlug, setTypedSlug] = useState('');

  // Clear between openings so a previous confirmation never carries over.
  useEffect(() => {
    if (open) setTypedSlug('');
  }, [open, page?.id]);

  const canDelete = page !== undefined && typedSlug.trim() === page.slug;

  const content = (
    <div className={bodyStyle}>
      <p className={textStyle}>
        Isso remove a página <span className={slugStyle}>{page?.slug}</span> de{' '}
        <span className={slugStyle}>{page?.tenantSlug}</span>. Anúncios e tours que apontam para ela
        deixam de ser entregues. Esta ação não pode ser desfeita.
      </p>

      {page?.isMain ? (
        <div className={alertStyle}>
          <span className={alertTitleStyle}>Esta é a página principal da BU</span>
          <span className={alertTextStyle}>
            É nela que caem os anúncios criados sem escolher página: sem uma principal, esses
            anúncios ficam sem página e só chegam ao dashboard quando ele pede sem informar a
            página. Defina outra como principal antes de excluir esta.
          </span>
        </div>
      ) : null}

      <UTTextField
        id='delete-page-confirm'
        topLabel={`Digite ${page?.slug ?? ''} para confirmar`}
        value={typedSlug}
        placeholder={page?.slug}
        onChange={event => {
          setTypedSlug(event.target.value);
        }}
      />

      <div className={footerStyle}>
        <UTButton fitContent variant='tertiary' text='Cancelar' onClick={onClose} />
        <UTButton
          fitContent
          id='delete-page-confirm-button'
          variant='primary'
          text={isDeleting ? 'Excluindo...' : 'Excluir página'}
          disabled={!canDelete || isDeleting}
          onClick={() => {
            if (page) onConfirm(page);
          }}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title='Excluir página'
      content={content}
      maxWidth='500px'
      hasButtonClose
      position='center'
    />
  );
};
